import { useState, useEffect } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { Alert, AlertDescription } from './ui/alert';
import { Plus, Pencil, Trash2, Database, Info } from 'lucide-react';
import { projectId } from '../utils/supabase/info';
import { generateDefaultConfigItems } from '../utils/defaultConfigItems';
import { DraggableConfigItem } from './DraggableConfigItem';
import type { ConfigItem, ConfigPhase, ConfigCategory } from '../types';

interface DefaultConfigItemsManagerProps {
  accessToken: string;
  currentUser?: any;
}

const CATEGORY_LABELS: Record<ConfigCategory, string> = {
  'sla': 'Service Level Agreements',
  'queue': 'Queues',
  'approval': 'Approval Flows',
  'asset': 'Asset Management',
  'automation': 'Automation Rules',
  'notification': 'Notifications',
  'email': 'Email Configuration',
  'portal': 'Customer Portal',
  'request-type-group': 'Request Type Groups',
  'knowledge-base': 'Knowledge Base',
  'integration': 'Integrations',
  'csat': 'Customer Satisfaction (CSAT)',
  'dashboard': 'Reports & Dashboards',
  'field-configuration': 'Field Configurations',
  'service-catalog': 'Service Catalog',
  'team-settings': 'Team & Agent Settings'
};

const PHASE_LABELS = {
  1: 'Phase 1 (Core)',
  2: 'Phase 2 (Automation)',
  3: 'Phase 3 (Portal)',
  4: 'Phase 4 (Advanced)',
  5: 'Future Consideration'
};

const PHASE_COLORS = {
  1: 'bg-blue-100 text-blue-800 border-blue-300',
  2: 'bg-green-100 text-green-800 border-green-300',
  3: 'bg-purple-100 text-purple-800 border-purple-300',
  4: 'bg-orange-100 text-orange-800 border-orange-300',
  5: 'bg-gray-100 text-gray-800 border-gray-300'
};

export function DefaultConfigItemsManager({ accessToken, currentUser }: DefaultConfigItemsManagerProps) {
  const [configItems, setConfigItems] = useState<ConfigItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ConfigItem | null>(null);
  const [filterPhase, setFilterPhase] = useState<ConfigPhase | 'all'>('all');
  const [filterCategory, setFilterCategory] = useState<ConfigCategory | 'all'>('all');

  // Form state
  const [formName, setFormName] = useState('');
  const [formCategory, setFormCategory] = useState<ConfigCategory>('sla');
  const [formPhase, setFormPhase] = useState<ConfigPhase>(1);
  const [formDescription, setFormDescription] = useState('');
  const [formRequiresAssets, setFormRequiresAssets] = useState(false);
  const [formCoreFeature, setFormCoreFeature] = useState(false);

  useEffect(() => {
    loadConfigItems();
  }, []);

  const loadConfigItems = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config/defaults`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setConfigItems(data.items || []);
      }
    } catch (error) {
      console.error('Error loading config items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const newItem: ConfigItem = {
      id: editingItem?.id || Date.now().toString(),
      name: formName,
      category: formCategory,
      defaultPhase: formPhase,
      description: formDescription,
      requiresAssets: formRequiresAssets,
      coreFeature: formCoreFeature
    };

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config/defaults`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ item: newItem }),
        }
      );

      if (response.ok) {
        await loadConfigItems();
        handleCloseDialog();
      }
    } catch (error) {
      console.error('Error saving config item:', error);
    }
  };

  const handleDelete = async (itemId: string) => {
    if (!confirm('Are you sure you want to delete this configuration item? This will affect all companies.')) {
      return;
    }

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config/defaults/${itemId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        await loadConfigItems();
      }
    } catch (error) {
      console.error('Error deleting config item:', error);
    }
  };

  const handleEdit = (item: ConfigItem) => {
    setEditingItem(item);
    setFormName(item.name);
    setFormCategory(item.category);
    setFormPhase(item.defaultPhase);
    setFormDescription(item.description);
    setFormRequiresAssets(item.requiresAssets || false);
    setFormCoreFeature(item.coreFeature || false);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingItem(null);
    setFormName('');
    setFormCategory('sla');
    setFormPhase(1);
    setFormDescription('');
    setFormRequiresAssets(false);
    setFormCoreFeature(false);
  };

  const handleSeedDefaults = async () => {
    if (!confirm('This will add all standard JSM configuration items to the system. Existing items will not be affected. Continue?')) {
      return;
    }

    const defaultItems = generateDefaultConfigItems();
    
    try {
      for (const item of defaultItems) {
        await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config/defaults`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ item }),
          }
        );
      }
      
      await loadConfigItems();
      alert(`Successfully added ${defaultItems.length} configuration items.`);
    } catch (error) {
      console.error('Error seeding defaults:', error);
      alert('Error seeding default configuration items. Please try again.');
    }
  };

  const handleMoveItem = async (itemId: string, fromPhase: ConfigPhase, toPhase: ConfigPhase, newIndex: number) => {
    const item = configItems.find(i => i.id === itemId);
    if (!item) return;

    // Update the item's phase
    const updatedItem = { ...item, defaultPhase: toPhase };
    
    // Optimistically update UI
    const updatedItems = configItems.map(i => i.id === itemId ? updatedItem : i);
    setConfigItems(updatedItems);

    // Save to backend
    try {
      await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config/defaults`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ item: updatedItem }),
        }
      );
    } catch (error) {
      console.error('Error moving config item:', error);
      // Revert on error
      await loadConfigItems();
    }
  };

  const handleReorderItem = async (itemId: string, fromIndex: number, toIndex: number, phase: ConfigPhase) => {
    // Get items in the current phase
    const phaseItems = configItems.filter(i => i.defaultPhase === phase);
    const otherItems = configItems.filter(i => i.defaultPhase !== phase);
    
    // Reorder within the phase
    const reorderedPhaseItems = [...phaseItems];
    const [movedItem] = reorderedPhaseItems.splice(fromIndex, 1);
    reorderedPhaseItems.splice(toIndex, 0, movedItem);
    
    // Combine and update
    const updatedItems = [...otherItems, ...reorderedPhaseItems];
    setConfigItems(updatedItems);
    
    // Note: Order is stored implicitly by the order items are returned
    // We could add an explicit order field if needed for persistence
  };

  const filteredItems = configItems.filter(item => {
    if (filterPhase !== 'all' && item.defaultPhase !== filterPhase) return false;
    if (filterCategory !== 'all' && item.category !== filterCategory) return false;
    return true;
  });

  const itemsByPhase = {
    1: filteredItems.filter(i => i.defaultPhase === 1),
    2: filteredItems.filter(i => i.defaultPhase === 2),
    3: filteredItems.filter(i => i.defaultPhase === 3),
    4: filteredItems.filter(i => i.defaultPhase === 4),
    5: filteredItems.filter(i => i.defaultPhase === 5)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <p className="text-muted-foreground">Loading configuration items...</p>
      </div>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3>Default Configuration Items</h3>
            <p className="text-muted-foreground">
              Define the standard configuration items for JSM implementations. These defaults apply to all companies unless overridden.
            </p>
          </div>
          <div className="flex gap-2">
            {configItems.length === 0 && (
              <Button variant="outline" onClick={handleSeedDefaults}>
                <Database className="mr-2 h-4 w-4" />
                Seed Defaults
              </Button>
            )}
            <Button onClick={() => setDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Configuration Item
            </Button>
          </div>
        </div>

        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            <strong>Drag and drop</strong> configuration items to reorder them within a phase or move them to a different phase.
          </AlertDescription>
        </Alert>

        {/* Filters */}
      <div className="flex gap-4">
        <div className="w-64">
          <Label>Filter by Phase</Label>
          <Select value={filterPhase.toString()} onValueChange={(v) => setFilterPhase(v as ConfigPhase | 'all')}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Phases</SelectItem>
              <SelectItem value="1">Phase 1 (Core)</SelectItem>
              <SelectItem value="2">Phase 2 (Automation)</SelectItem>
              <SelectItem value="3">Phase 3 (Portal)</SelectItem>
              <SelectItem value="4">Phase 4 (Advanced)</SelectItem>
              <SelectItem value="5">Future Consideration</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-64">
          <Label>Filter by Category</Label>
          <Select value={filterCategory} onValueChange={(v) => setFilterCategory(v as ConfigCategory | 'all')}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                <SelectItem key={key} value={key}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Items by Phase */}
      <div className="space-y-6">
        {([1, 2, 3, 4, 5] as ConfigPhase[]).map(phase => (
          <Card key={phase}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Badge className={PHASE_COLORS[phase]}>
                  {PHASE_LABELS[phase]}
                </Badge>
                <span className="text-muted-foreground">
                  ({itemsByPhase[phase].length} items)
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {itemsByPhase[phase].length === 0 ? (
                <p className="text-muted-foreground">No items in this phase. Drag items here to move them to this phase.</p>
              ) : (
                <div className="space-y-3">
                  {itemsByPhase[phase].map((item, index) => (
                    <DraggableConfigItem
                      key={item.id}
                      item={item}
                      index={index}
                      phase={phase}
                      onMoveItem={handleMoveItem}
                      onReorderItem={handleReorderItem}
                      renderActions={(item) => (
                        <div className="flex gap-2 flex-shrink-0">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(item)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ))}\n      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit' : 'Add'} Configuration Item</DialogTitle>
            <DialogDescription>
              Define a configuration item for JSM implementations. This will be the default for all companies.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="e.g., SLA for Incidents"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select value={formCategory} onValueChange={(v) => setFormCategory(v as ConfigCategory)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Default Phase</Label>
                <Select value={formPhase.toString()} onValueChange={(v) => setFormPhase(parseInt(v) as ConfigPhase)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Phase 1 (Core)</SelectItem>
                    <SelectItem value="2">Phase 2 (Automation)</SelectItem>
                    <SelectItem value="3">Phase 3 (Portal)</SelectItem>
                    <SelectItem value="4">Phase 4 (Advanced)</SelectItem>
                    <SelectItem value="5">Future Consideration</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="What does this configuration item do?"
                rows={3}
              />
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="requires-assets"
                  checked={formRequiresAssets}
                  onCheckedChange={(checked) => setFormRequiresAssets(checked as boolean)}
                />
                <Label htmlFor="requires-assets">Requires Jira Assets</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="core-feature"
                  checked={formCoreFeature}
                  onCheckedChange={(checked) => setFormCoreFeature(checked as boolean)}
                />
                <Label htmlFor="core-feature">Core Feature (always recommended)</Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button onClick={handleSave}>
              {editingItem ? 'Update' : 'Add'} Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </DndProvider>
  );
}
